/* ============================================================
   Dathent — dashboardHydration.js
   Owner finding (staging 2026-09-08): on the Dashboard, for an org with an
   ACTIVE subscription and a COMPLETE brand (research ready, plan built),
   the "Building your setup" activation card (ActivationProgressCard, decision
   from lib/activationProgress.js decideActivationProgress) briefly flashed
   its full in-progress checklist ("Reading your website…", "Starting market
   research…", "Drafting your content plan", "Creating your first post")
   before disappearing once data arrived.

   Root cause: ActivationProgressCard reads `postCount` straight off
   ContentFeedContext.counts.all without checking that context's OWN initial
   fetch has actually resolved — counts starts at emptyContentCounts() (every
   field 0) and only reflects the truth once ContentFeedContext.loading flips
   to false. decideActivationProgress's one "fully activated, nothing to
   narrate" short-circuit is `hasPlan && postCount > 0` — while postCount is
   still the stale 0 default, that guard can miss even though the org is
   already fully set up, letting the in-progress narration render for a beat.
   The card already gates its OWN profile/research/strategy probes behind its
   internal `loaded` state and gates on `dashState.fetchedAt.entitlements`
   (`planKnown`) before computing a decision at all — postCount was the one
   input with no such gate.

   This is a pure, render-free helper so it can be unit-tested without a
   component-rendering harness (this repo has no @testing-library/react
   dependency) — Dashboard.jsx derives `dashboardHydrated` from it, using the
   same provider states it already reads for its own scenario/skeleton logic
   (dashState.fetchedAt.entitlements, ContentFeedContext.loading), and only
   mounts <ActivationProgressCard/> once it's true. Behaviour once hydrated
   is unchanged — this only delays the card's first mount, never alters what
   it decides to show.
   ============================================================ */

/**
 * @param {object} opts
 * @param {boolean} [opts.entitlementsFetched]  dashState.fetchedAt?.entitlements
 *        truthy — mirrors the `planKnown` gate ActivationProgressCard and
 *        Dashboard.jsx already use elsewhere.
 * @param {boolean} [opts.contentFeedLoading]  ContentFeedContext.loading —
 *        true until the content feed's initial fetch (which populates
 *        `counts`, the source of ActivationProgressCard's `postCount`) has
 *        resolved at least once.
 * @returns {boolean} true once every input the activation decision depends
 *        on has actually loaded, false while any of them is still pending.
 */
export function isDashboardHydrated({ entitlementsFetched = false, contentFeedLoading = true } = {}) {
  return Boolean(entitlementsFetched) && !contentFeedLoading;
}
